import {
  Box,
  Circle2d,
  DrawShapeUtil,
  Polygon2d,
  Polyline2d,
  SVGContainer,
  getColorValue,
  getDefaultColorTheme,
  getSvgPathFromStrokePoints,
  useDefaultColorTheme,
  type SvgExportContext,
  type TLDrawShape,
  type VecLike,
} from "tldraw";

import { inkStrokePoints, inkStrokeWidth, isPlainInk } from "./inkStroke";

/**
 * tldraw's draw shape with the ink pipeline from inkStroke.ts. tldraw fixes
 * its freehand options in a module-level function, so changing them means
 * owning every path: geometry, render, indicator and export. All four use
 * `inkStrokePoints`, so what is drawn is what is selected and exported.
 * Registered as "draw", it replaces the built-in util (utils merge by type).
 * Plain ink only; everything else defers to the base class.
 */
export class InkDrawShapeUtil extends DrawShapeUtil {
  override getGeometry(shape: TLDrawShape) {
    if (!isPlainInk(shape)) return super.getGeometry(shape);

    const sw = inkStrokeWidth(shape);
    const points = inkStrokePoints(shape).map((p) => p.point);

    if (points.length <= 1 || isDot(shape, points, sw)) {
      return new Circle2d({ x: -sw, y: -sw, radius: sw, isFilled: true });
    }
    if (shape.props.isClosed && points.length > 2) {
      return new Polygon2d({ points, isFilled: false });
    }
    return new Polyline2d({ points });
  }

  override component(shape: TLDrawShape) {
    if (!isPlainInk(shape)) return super.component(shape);
    return (
      <SVGContainer>
        <InkPath shape={shape} />
      </SVGContainer>
    );
  }

  // The base class opts out of legacy indicators, so only getIndicatorPath
  // draws the selection outline; `indicator()` is never called.
  override getIndicatorPath(shape: TLDrawShape): Path2D {
    if (!isPlainInk(shape)) return super.getIndicatorPath(shape);
    return new Path2D(inkPath(shape));
  }

  override toSvg(shape: TLDrawShape, ctx: SvgExportContext) {
    if (!isPlainInk(shape)) return super.toSvg(shape, ctx);
    const theme = getDefaultColorTheme({ isDarkMode: ctx.isDarkMode });
    return (
      <g transform={`scale(${1 / shape.props.scale})`}>
        <InkPathSvg
          shape={shape}
          color={getColorValue(theme, shape.props.color, "solid")}
        />
      </g>
    );
  }
}

/** A single tap: one segment whose extent is under a stroke width. */
function isDot(shape: TLDrawShape, points: VecLike[], sw: number): boolean {
  if (shape.props.segments.length !== 1) return false;
  const box = Box.FromPoints(points);
  return box.width < sw * 2 && box.height < sw * 2;
}

/** The centreline as SVG path data; a lone point becomes a round dot. */
function inkPath(shape: TLDrawShape): string {
  const strokePoints = inkStrokePoints(shape);
  if (strokePoints.length === 0) return "";
  if (strokePoints.length === 1) return dotPath(strokePoints[0].point);
  return getSvgPathFromStrokePoints(strokePoints, shape.props.isClosed);
}

/** A zero-length subpath at `p`; round line caps swell it to a full dot. */
function dotPath(p: VecLike): string {
  return `M ${p.x} ${p.y} l 0 0`;
}

function InkPath({ shape }: { shape: TLDrawShape }) {
  const theme = useDefaultColorTheme();
  return (
    <InkPathSvg
      shape={shape}
      color={getColorValue(theme, shape.props.color, "solid")}
    />
  );
}

function InkPathSvg({ shape, color }: { shape: TLDrawShape; color: string }) {
  return (
    <path
      d={inkPath(shape)}
      fill="none"
      stroke={color}
      strokeWidth={inkStrokeWidth(shape)}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}
